import clsx from "clsx";
import { useState } from "react";
import { Popover as AriaPopover, Dialog, DialogTrigger, type Key } from "react-aria-components";
import useMeasure from "react-use-measure";

import { InputHeader } from "@/components/ui/inputs/shared/InputHeader";

import type { DropdownProps } from "./Dropdown";
import { DropdownButton } from "./DropdownButton";
import { DropdownListBox } from "./DropdownListBox";

export const DropdownDesktop = <TKey extends Key = Key>(props: DropdownProps<TKey>) => {
  const {
    label,
    items,
    placeholder,
    value,
    selectionMode,
    variant,
    customTrigger,
    customTriggerWidth = undefined,
    className,
    width: containerWidth,
    containerClassName,
    "data-testid": dataTestId,
    hideLabels,
    required,
    isOpen: controlledIsOpen,
    onOpenChange: controlledOnOpenChange,
  } = props;

  const selectedItems = items.filter((item) =>
    selectionMode === "multiple" ? value.includes(item.id) : item.id === value,
  );

  const [triggerRef, { width }] = useMeasure();

  const [internalIsOpen, setInternalIsOpen] = useState(false);

  // Use controlled state if provided, otherwise use internal state
  const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;
  const handleOpenChange = (open: boolean) => {
    if (controlledOnOpenChange) {
      controlledOnOpenChange(open);
    } else {
      setInternalIsOpen(open);
    }
  };

  const onChange: DropdownProps<TKey>["onChange"] = (val: typeof value) => {
    if (selectionMode === "multiple") {
      props.onChange(val as TKey[]);
    } else {
      props.onChange(val as TKey);
      handleOpenChange(false);
    }
  };

  const listboxProps = {
    ...props,
    onChange,
  } as DropdownProps<TKey>;

  return (
    <div className={clsx("group", containerClassName, { "w-full": containerWidth === "full" })}>
      {label && (
        <InputHeader
          label={label}
          hideLabels={hideLabels}
          required={required}
        />
      )}

      <DialogTrigger
        isOpen={isOpen}
        onOpenChange={handleOpenChange}
      >
        {customTrigger ?? (
          <DropdownButton
            ref={triggerRef}
            variant={variant}
            className={className}
            data-testid={dataTestId}
          >
            <span className={selectedItems.length > 0 ? "" : "text-secondary-100"}>
              {selectedItems.length > 0 ? selectedItems.map((item) => item.label).join(", ") : placeholder}
            </span>
          </DropdownButton>
        )}

        <AriaPopover
          className="my-4 outline-hidden"
          style={{ width: !customTrigger ? width : customTriggerWidth }}
          offset={-10}
        >
          <Dialog className="outline-hidden">
            <div className="rounded-input-rounding bg-elevation-fill-default-1 shadow-5 outline-hidden">
              <DropdownListBox {...listboxProps} />
            </div>
          </Dialog>
        </AriaPopover>
      </DialogTrigger>
    </div>
  );
};
